/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/PublicHolidaysModern.tsx
 * PURPOSE: SC-032-EMP-PUBLIC-HOLIDAYS — employee read-only lookup of official
 *          public holidays for a year (BS-032-R01..R05). New screen, web +
 *          mobile share ONE definition (registry row 26) — this is the web
 *          half; mobile half is mobile/src/screens/PublicHolidaysScreen.tsx.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-24
 * COMPLIANCE: SDLC 6.5.0 + BS-024-PHAN-DOAN-D-NHAN-VIEN-NGHI-PHEP §BS-032-R01..R05
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management, Employee Self-Service Leave
 * - Impact: Prevents employees from booking leave over official holidays
 *   (they don't count against phép năm — BS-024-R08/R09)
 * - Stakeholders: Service Staff (NQH), Branch Managers, Tenant Admins/Owners (read-only here)
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React Router, Page Layout — mirrors LeaveRequestModern.tsx conventions
 * - Dependencies: React 18, React Router, TypeScript, services/api/leaveRequest
 *
 * @cross-references
 * - Design: docs/01-planning/02-uat-len-lich-cham-cong/BS-024-PHAN-DOAN-D-NHAN-VIEN-NGHI-PHEP.md §BS-032
 * - Wireframe: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-MASTER-REGISTRY.md §SC-032-EMP-PUBLIC-HOLIDAYS
 * - API: backend/app/api/v1/public_holiday.py (`GET /api/v1/public-holidays?year=`)
 * - Service: frontend/src/services/api/leaveRequest.ts (getPublicHolidays)
 * - Mobile counterpart: mobile/src/screens/PublicHolidaysScreen.tsx
 * - Entry points: frontend/src/pages/LeaveRequestModern.tsx ([10] section link),
 *   frontend/src/pages/Utilities.tsx ("Ngày lễ" tile)
 * - Tests: frontend/tests/pages/PublicHolidaysModern.test.tsx
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { EmployeeBottomNav } from '../components/navigation/EmployeeBottomNav';
import styles from './PublicHolidaysModern.module.css';
import { getPublicHolidays, type PublicHolidayDTO } from '../services/api/leaveRequest';

type PublicHoliday = PublicHolidayDTO;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function formatDmy(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/** [5] "{DD/MM}-{DD/MM/YYYY} {tên lễ} ({N} ngày)" khi nhiều ngày, "{DD/MM/YYYY} {tên lễ}" khi 1 ngày. */
function formatHolidayRange(h: PublicHoliday): string {
  if (!h.end_date || h.end_date === h.start_date) {
    return formatDmy(h.start_date);
  }
  const start = new Date(h.start_date);
  const end = new Date(h.end_date);
  const days = Math.round((end.getTime() - start.getTime()) / MS_PER_DAY) + 1;
  const shortStart = h.start_date.slice(5).split('-').reverse().join('/'); // DD/MM
  return `${shortStart}-${formatDmy(h.end_date)} (${days} ngày)`;
}

/** BS-032-R04: ngày còn lại tới start_date của 1 ngày lễ, tính theo lịch device. */
function daysUntil(isoDate: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / MS_PER_DAY);
}

export const PublicHolidaysModern: React.FC = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  // BS-032-R02: 3 chip năm trước/hiện tại/sau, mặc định năm hiện tại.
  const [year, setYear] = useState(currentYear);
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (targetYear: number) => {
    setLoading(true);
    setError(null);
    try {
      const items = await getPublicHolidays(targetYear);
      setHolidays(items);
    } catch {
      setError('Không thể tải danh sách ngày nghỉ lễ.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(year);
  }, [year, load]);

  // BS-032-R04: banner "Sắp đến" — chỉ khi có lễ trong 7 ngày tới, hiện lễ
  // GẦN NHẤT trong số đó.
  const upcoming = holidays
    .filter(h => {
      const d = daysUntil(h.start_date);
      return d >= 0 && d <= 7;
    })
    .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];

  // BS-032-R02: "3 chip: năm trước/hiện tại/sau" — cố định quanh năm hiện
  // tại thật (KHÔNG dịch theo `year` đang chọn).
  const fixedYearOptions = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={styles.headerBackButton}
            aria-label="Quay lại"
            data-testid="public-holidays-back"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <h1 id="public-holidays-title" className={styles.title}>
            Các ngày nghỉ lễ · {year}
          </h1>
        </div>
      </header>

      <main aria-labelledby="public-holidays-title" className={styles.main}>
        {/* [2] Bộ chọn năm */}
        <div className={styles.yearChipGroup} role="radiogroup" aria-label="Chọn năm">
          {fixedYearOptions.map(y => (
            <button
              key={y}
              type="button"
              role="radio"
              aria-checked={year === y}
              className={`${styles.yearChip} ${year === y ? styles.yearChipSelected : ''}`}
              onClick={() => setYear(y)}
              data-testid={`public-holidays-year-${y}`}
            >
              {y}
            </button>
          ))}
        </div>

        {loading && (
          <div className={styles.card} data-testid="public-holidays-skeleton">
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
            <div className={styles.skeleton} />
          </div>
        )}

        {!loading && error && (
          <p className={styles.statusError} data-testid="public-holidays-error">
            <span>⚠ {error}</span>
            <button type="button" className={styles.retryButton} onClick={() => load(year)}>
              Thử lại
            </button>
          </p>
        )}

        {!loading && !error && (
          <>
            {/* [3] Banner "Sắp đến" — chỉ khi có */}
            {upcoming && (
              <p className={styles.upcomingBanner} data-testid="public-holidays-upcoming-banner">
                📅 Sắp đến: {upcoming.name} ({formatDmy(upcoming.start_date)}) — còn {daysUntil(upcoming.start_date)} ngày
              </p>
            )}

            {holidays.length === 0 ? (
              /* BS-032-R05: EMPTY state — năm chưa có dữ liệu, KHÔNG phải lỗi */
              <div className={styles.card}>
                <div className={styles.emptyState} data-testid="public-holidays-empty">
                  <p className={styles.emptyStateTitle}>📭 Chưa có dữ liệu ngày lễ cho năm {year}</p>
                  <p className={styles.emptyStateHint}>Vui lòng liên hệ Quản trị viên để biết thêm</p>
                </div>
              </div>
            ) : (
              <div className={styles.card}>
                <ul className={styles.holidayList}>
                  {holidays.map(h => (
                    <li key={h.id} className={styles.holidayRow} data-testid={`public-holiday-${h.id}`}>
                      <span className={styles.holidayDate}>{formatHolidayRange(h)}</span>
                      <span className={styles.holidayName}>{h.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </main>

      <EmployeeBottomNav />
    </div>
  );
};

export default PublicHolidaysModern;
